// The shop's own questions (TW-0412, 2026-10-20).
//
// Perfecto Bliss (Maryland, tattoos and piercings), a paying shop, that morning: "We'd like to add:
// 'Have you eaten in the last 3 hours?'" Chris, the same hour: "i could see it not being a required
// checkbox, but being a Yes / No and then showing a disclaimer? in my experience it was good to eat
// before, but i don't know the intent."
//
// `public_data.own_questions` is `[{id, text, when, note}, …]` in the owner's words, written from the
// shop app's Settings → Waiver form → Your own questions: a yes-or-no question, and an optional line
// the owner writes (advice, a disclaimer) that shows under the answer `when` names. The form draws each
// one among the health questions, after every one the page asks of its own, as a Yes / No the client
// picks; the line appears under the chosen answer when it is the answer the line names, and NEVER
// stops a signing. The page posts `ownQuestions: {<id>: "yes" | "no"}` for every question it drew; the
// API refuses a blank (OWN_QUESTION_UNANSWERED), keeps each answer on the waiver row beside the
// question's words, and prints both on the signed waiver from the shop's row by that id
// (services/waiver-api, app.py). THE OWNER'S WORDS ARE SHOWN AS WRITTEN IN EITHER LANGUAGE, the own
// clauses' rule (ownClauses.ts): nobody here translates a shop's words; the Yes and the No follow the
// page's language, and a blank is refused in the health questions' own sentence (i18n.ts
// needHealthAnswer: "Answer yes or no: <the question>"). This module is the only place that decides
// what the key means: the rest of the form asks it.

export type OwnAnswer = "yes" | "no";
export type OwnQuestion = { id: string; text: string; when: OwnAnswer | null; note: string };

/** At most this many, the API's MAX_OWN_QUESTIONS; a row that somehow carries more shows the first. */
export const MAX_OWN_QUESTIONS = 5;

const ID = /^oq_[0-9a-f]{10}$/;

const oneLine = (value: unknown): string => `${value ?? ""}`.split(/\s+/).join(" ").trim();

/** The answer a line shows under, the API's way (common.clean_own_question_when): "no" or "yes", case and spaces forgiven. */
function answerWord(value: unknown): OwnAnswer | null {
  const word = typeof value === "string" ? value.trim().toLowerCase() : "";
  return word === "yes" || word === "no" ? word : null;
}

/** The questions a shop's config carries, read tolerantly, the API's way (common.own_questions_of): a junk item is skipped
 *  and a line that names no answer is dropped, never fatal, so a hand-written row can never blank a form; an answer picked
 *  with no line yet is kept and shows nothing. Absent, null or anything but a list is none, which is every shop that has not
 *  written one. */
export function ownQuestionsOf(value: unknown): OwnQuestion[] {
  if (!Array.isArray(value)) return [];
  const out: OwnQuestion[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const raw = item as { id?: unknown; text?: unknown; when?: unknown; note?: unknown };
    const id = `${raw.id ?? ""}`.trim();
    const text = oneLine(raw.text);
    if (!ID.test(id) || !text || seen.has(id)) continue;
    const when = answerWord(raw.when);
    const note = when && typeof raw.note === "string" ? oneLine(raw.note) : "";
    seen.add(id);
    out.push({ id, text, when, note });
    if (out.length === MAX_OWN_QUESTIONS) break;
  }
  return out;
}

/** The line under the client's answer: the question's line, under the answer it names; "" under every other answer, before
 *  any answer, and for a question with no line. The API reads the same rule (common.own_question_note_shown). */
export function ownQuestionNoteShown(question: OwnQuestion, answer: string | undefined): string {
  return question.note && question.when && answer === question.when ? question.note : "";
}

/** What the body posts: every question the page drew, by its id, with the client's answer or "" for none (the submit
 *  refuses a blank first, and the API refuses one too). */
export function ownQuestionsPosted(questions: OwnQuestion[], answers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(questions.map((question) => [question.id, answers[question.id] === "yes" || answers[question.id] === "no" ? answers[question.id] : ""]));
}

/** The first question drawn with neither a yes nor a no: where the refusal takes the client. */
export function firstOwnQuestionUnanswered(questions: OwnQuestion[], answers: Record<string, string>): OwnQuestion | undefined {
  return questions.find((question) => answers[question.id] !== "yes" && answers[question.id] !== "no");
}

/** The radios' shared name, which is what a refusal finds (refusal.ts own_question_unanswered). */
export const ownQuestionInputName = (question: Pick<OwnQuestion, "id">): string => `own-question.${question.id}`;
